import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { CUSTOMER_TYPE } from "../constants/customer.constant";
import { getSiteDisplayName } from "../constants/siteDisplayNames";
import { compareMedicalFormCustomersByName, displayYesNoValue } from "./medicalCustomerSheetSave";
import {
  ensureExtension,
  PDF_UNICODE_FONT_NAME,
  registerPdfUnicodeFont,
  triggerBlobDownload,
  wrapTextForColumn,
} from "./medicalSheetCommon";
import { formatTbCustomerName } from "./tbSheetExport";

export const CENTER_SECTION_TITLE = "Medications given at the center";
export const BBP_SECTION_TITLE = "Receive BBP from Vital Care";
export const CENTER_HEADERS = ["No.", "Customer", "Type", "Medication Note"];
export const BBP_HEADERS = ["No.", "Customer", "Pillbox Type", "Pillbox Note"];

const COLUMN_COUNT = 4;
const BASE_COL_WIDTHS_PX = [48, 260, 140, 280];
const TITLE_ROW_HEIGHT_PX = 44;
const SECTION_ROW_HEIGHT_PX = 34;
const HEADER_ROW_HEIGHT_PX = 34;
const MIN_ROW_HEIGHT_PX = 36;
const LINE_HEIGHT_PX = 18;
const SECTION_BG = "#9DC3E6";
const COL_HEADER_BG = "#DDEBF7";
const SECTION_RGB = [157, 195, 230];
const COL_HEADER_RGB = [221, 235, 247];
const LINE_COLOR = [51, 51, 51];
const BORDER_80 = "FF333333";
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const border80 = {
  top: { style: "thin", color: { argb: BORDER_80 } },
  left: { style: "thin", color: { argb: BORDER_80 } },
  bottom: { style: "thin", color: { argb: BORDER_80 } },
  right: { style: "thin", color: { argb: BORDER_80 } },
};

function formatExportedOnDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${MONTHS[valid.getMonth()]} ${String(valid.getDate()).padStart(2, "0")}, ${valid.getFullYear()}`;
}

export function buildMedicationTitle(siteNum, exportedAt = new Date()) {
  const site = getSiteDisplayName(siteNum);
  return `${site} Medication Management (Exported on ${formatExportedOnDate(exportedAt)})`;
}

export function isMedicationRosterCustomer(customer) {
  if (!customer) return false;
  const status = `${customer.status || ""}`.trim().toLowerCase();
  if (status !== "active") return false;
  const type = `${customer.type || ""}`.trim().toLowerCase();
  return type === CUSTOMER_TYPE.MEMBER;
}

function isYes(value) {
  return displayYesNoValue(value) === "Yes";
}

function sortCustomersByName(customers = []) {
  return [...customers].sort((a, b) => compareMedicalFormCustomersByName(a, b));
}

function cellText(value) {
  return `${value ?? ""}`.replace(/\s+/g, " ").trim();
}

function customerIdOf(customer) {
  return `${customer?._id ?? customer?.id ?? ""}`.trim();
}

function centerRowFromCustomer(customer, no) {
  return {
    id: customerIdOf(customer),
    no,
    name: formatTbCustomerName(customer),
    type: cellText(customer?.medication_type),
    note: cellText(customer?.medication_note),
  };
}

function bbpRowFromCustomer(customer, no) {
  return {
    id: customerIdOf(customer),
    no,
    name: formatTbCustomerName(customer),
    type: cellText(customer?.pillbox_type),
    note: cellText(customer?.pillbox_note),
  };
}

export function buildMedicationSections(customers = []) {
  const eligible = sortCustomersByName((customers || []).filter(isMedicationRosterCustomer));
  const centerRows = [];
  const bbpRows = [];
  eligible.forEach((customer) => {
    if (isYes(customer?.medications_given_at_center)) {
      centerRows.push(centerRowFromCustomer(customer, centerRows.length + 1));
    }
    if (isYes(customer?.pillbox)) {
      bbpRows.push(bbpRowFromCustomer(customer, bbpRows.length + 1));
    }
  });
  return { centerRows, bbpRows };
}

export const emptyMedicationSections = { centerRows: [], bbpRows: [] };

export function medicationSheetLayout(sections) {
  const centerCount = (sections?.centerRows || []).length;
  const bbpCount = (sections?.bbpRows || []).length;
  const centerDataStart = 3;
  const bbpSectionRow = centerDataStart + Math.max(1, centerCount);
  const bbpHeaderRow = bbpSectionRow + 1;
  const bbpDataStart = bbpHeaderRow + 1;
  return {
    centerDataStart,
    centerCount,
    bbpSectionRow,
    bbpHeaderRow,
    bbpDataStart,
    bbpCount,
  };
}

function readSheetCellText(sheetRows, r, c) {
  const cell = sheetRows?.[r]?.[c];
  if (cell == null) return "";
  const raw = typeof cell === "object" ? `${cell.m ?? cell.v ?? ""}` : `${cell}`;
  return cellText(raw);
}

function parseSectionRows(sheetRows, startRow, baselineRows) {
  return (baselineRows || []).map((row, index) => ({
    ...row,
    type: readSheetCellText(sheetRows, startRow + index, 2),
    note: readSheetCellText(sheetRows, startRow + index, 3),
  }));
}

export function parseMedicationSectionsFromWorkbook(workbook, baselineSections) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) {
    return {
      centerRows: [...(baselineSections?.centerRows || [])],
      bbpRows: [...(baselineSections?.bbpRows || [])],
    };
  }
  const layout = medicationSheetLayout(baselineSections);
  return {
    centerRows: parseSectionRows(sheetRows, layout.centerDataStart, baselineSections?.centerRows),
    bbpRows: parseSectionRows(sheetRows, layout.bbpDataStart, baselineSections?.bbpRows),
  };
}

function medicationRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i] || {};
    return (
      `${row?.id ?? ""}` === `${other?.id ?? ""}` &&
      cellText(row?.type) === cellText(other?.type) &&
      cellText(row?.note) === cellText(other?.note)
    );
  });
}

export function medicationSectionsEqual(a, b) {
  return (
    medicationRowsEqual(a?.centerRows, b?.centerRows) &&
    medicationRowsEqual(a?.bbpRows, b?.bbpRows)
  );
}

function mergeCustomerUpdate(map, customerId, fields) {
  const id = `${customerId ?? ""}`.trim();
  if (!id || !fields || Object.keys(fields).length === 0) return;
  const prev = map.get(id) || { customerId: id };
  map.set(id, { ...prev, ...fields });
}

function collectSectionUpdates(draftRows, baselineRows, typeKey, noteKey, map) {
  const byId = new Map((baselineRows || []).map((row) => [`${row?.id ?? ""}`.trim(), row]));
  (draftRows || []).forEach((row) => {
    const id = `${row?.id ?? ""}`.trim();
    const prev = byId.get(id);
    if (!id || !prev) return;
    const fields = {};
    if (cellText(row.type) !== cellText(prev.type)) fields[typeKey] = cellText(row.type);
    if (cellText(row.note) !== cellText(prev.note)) fields[noteKey] = cellText(row.note);
    mergeCustomerUpdate(map, id, fields);
  });
}

export function collectMedicationCustomerUpdates(draft, baseline) {
  const map = new Map();
  collectSectionUpdates(
    draft?.centerRows,
    baseline?.centerRows,
    "medication_type",
    "medication_note",
    map
  );
  collectSectionUpdates(draft?.bbpRows, baseline?.bbpRows, "pillbox_type", "pillbox_note", map);
  return [...map.values()];
}

function rowToCells(row) {
  return [`${row?.no ?? ""}`, `${row?.name ?? ""}`, `${row?.type ?? ""}`, `${row?.note ?? ""}`];
}

function cellValue(text, extras = {}) {
  const value = `${text ?? ""}`;
  return {
    v: value,
    m: value,
    ct: { fa: "@", t: "s" },
    ff: extras.ff || "Arial",
    fs: extras.fs || 12,
    bl: extras.bl ? 1 : 0,
    ht: extras.ht ?? 0,
    vt: extras.vt ?? 0,
    tb: "2",
    ...(extras.bg ? { bg: extras.bg } : {}),
    ...(extras.fc ? { fc: extras.fc } : {}),
    ...(extras.mc ? { mc: extras.mc } : {}),
  };
}

function pushMergedBanner(celldata, merge, r, text, extras = {}) {
  const mc = { r, c: 0, rs: 1, cs: COLUMN_COUNT };
  celldata.push({
    r,
    c: 0,
    v: cellValue(text, { ...extras, ht: 0, bl: true, mc }),
  });
  for (let c = 1; c < COLUMN_COUNT; c += 1) {
    celldata.push({
      r,
      c,
      v: {
        ct: { fa: "@", t: "s" },
        bg: extras.bg,
        mc,
      },
    });
  }
  merge[`${r}_0`] = { ...mc };
}

function pushHeaderRow(celldata, r, headers) {
  headers.forEach((header, c) => {
    celldata.push({
      r,
      c,
      v: cellValue(header, { bl: true, ht: 0, bg: COL_HEADER_BG }),
    });
  });
}

function pushDataRow(celldata, r, cells, rowlen, customHeight) {
  let maxLines = 1;
  cells.forEach((value, c) => {
    const wrapped = wrapTextForColumn(value, BASE_COL_WIDTHS_PX[c] || 100);
    if (wrapped.lines > maxLines) maxLines = wrapped.lines;
    const text = wrapped.text || `${value ?? ""}`;
    const ht = c === 1 || c === 3 ? 1 : 0;
    celldata.push({
      r,
      c,
      v: cellValue(text, { ht }),
    });
  });
  rowlen[`${r}`] = Math.max(MIN_ROW_HEIGHT_PX, maxLines * LINE_HEIGHT_PX);
  customHeight[`${r}`] = 1;
}

export function buildMedicationFortuneSheetData(sections, siteNum, exportedAt = new Date()) {
  const { centerRows = [], bbpRows = [] } = sections || {};
  const celldata = [];
  const merge = {};
  const rowlen = {};
  const customHeight = {};
  const columnlen = {};
  BASE_COL_WIDTHS_PX.forEach((width, index) => {
    columnlen[`${index}`] = width;
  });

  const title = buildMedicationTitle(siteNum, exportedAt);
  pushMergedBanner(celldata, merge, 0, title, {
    ff: "Times New Roman",
    fs: 16,
    bg: "#FFFFFF",
  });
  rowlen["0"] = TITLE_ROW_HEIGHT_PX;
  customHeight["0"] = 1;

  let r = 1;
  pushMergedBanner(celldata, merge, r, CENTER_SECTION_TITLE, { bg: SECTION_BG, fs: 12 });
  rowlen[`${r}`] = SECTION_ROW_HEIGHT_PX;
  customHeight[`${r}`] = 1;
  r += 1;

  pushHeaderRow(celldata, r, CENTER_HEADERS);
  rowlen[`${r}`] = HEADER_ROW_HEIGHT_PX;
  customHeight[`${r}`] = 1;
  r += 1;

  (centerRows.length > 0 ? centerRows : [{ no: "", name: "", type: "", note: "" }]).forEach((row) => {
    pushDataRow(celldata, r, rowToCells(row), rowlen, customHeight);
    r += 1;
  });

  pushMergedBanner(celldata, merge, r, BBP_SECTION_TITLE, { bg: SECTION_BG, fs: 12 });
  rowlen[`${r}`] = SECTION_ROW_HEIGHT_PX;
  customHeight[`${r}`] = 1;
  r += 1;

  pushHeaderRow(celldata, r, BBP_HEADERS);
  rowlen[`${r}`] = HEADER_ROW_HEIGHT_PX;
  customHeight[`${r}`] = 1;
  r += 1;

  (bbpRows.length > 0 ? bbpRows : [{ no: "", name: "", type: "", note: "" }]).forEach((row) => {
    pushDataRow(celldata, r, rowToCells(row), rowlen, customHeight);
    r += 1;
  });

  return [
    {
      name: "Medication",
      celldata,
      row: Math.max(40, r + 4),
      column: COLUMN_COUNT,
      config: {
        merge,
        rowlen,
        customHeight,
        columnlen,
        rowhidden: { "0": 0 },
      },
      frozen: {
        type: "row",
        range: { row_focus: 0, column_focus: 0 },
      },
      scrollLeft: 0,
      scrollTop: 0,
      luckysheet_select_save: [],
    },
  ];
}

function sectionHead(title, headers) {
  return [
    [
      {
        content: title,
        colSpan: COLUMN_COUNT,
        styles: {
          fillColor: SECTION_RGB,
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
        },
      },
    ],
    headers.map((header) => ({
      content: header,
      styles: {
        fillColor: COL_HEADER_RGB,
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      },
    })),
  ];
}

function bodyFromRows(rows) {
  const list = rows || [];
  if (list.length === 0) return [["", "", "", ""]];
  return list.map((row) => rowToCells(row));
}

export async function downloadMedicationPdf(sections, siteNum, filename = "Medication_Management") {
  const title = buildMedicationTitle(siteNum, new Date());
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  await registerPdfUnicodeFont(doc);

  const marginX = 36;
  const marginY = 28;
  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = pageWidth - marginX * 2;

  doc.setFont(PDF_UNICODE_FONT_NAME, "bold");
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  const titleLines = doc.splitTextToSize(title, tableWidth);
  const titleY = marginY + 13;
  doc.text(titleLines, pageWidth / 2, titleY, { align: "center" });

  const columnStyles = {
    0: { cellWidth: 36, halign: "center" },
    1: { cellWidth: 200, halign: "left" },
    2: { cellWidth: 110, halign: "center" },
    3: { cellWidth: tableWidth - 36 - 200 - 110, halign: "left" },
  };

  const tableOptions = {
    tableWidth,
    theme: "grid",
    styles: {
      font: PDF_UNICODE_FONT_NAME,
      fontStyle: "normal",
      fontSize: 10,
      cellPadding: 3,
      valign: "middle",
      overflow: "linebreak",
      lineColor: LINE_COLOR,
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      minCellHeight: 18,
    },
    headStyles: {
      font: PDF_UNICODE_FONT_NAME,
      fontStyle: "bold",
      fontSize: 10,
      fillColor: COL_HEADER_RGB,
      textColor: [0, 0, 0],
      halign: "center",
      valign: "middle",
      lineColor: LINE_COLOR,
      lineWidth: 0.5,
      minCellHeight: 22,
    },
    columnStyles,
    margin: { left: marginX, right: marginX, bottom: marginY },
    rowPageBreak: "avoid",
  };

  autoTable(doc, {
    ...tableOptions,
    startY: titleY + titleLines.length * 16,
    head: sectionHead(CENTER_SECTION_TITLE, CENTER_HEADERS),
    body: bodyFromRows(sections?.centerRows),
  });

  autoTable(doc, {
    ...tableOptions,
    startY: doc.lastAutoTable.finalY,
    head: sectionHead(BBP_SECTION_TITLE, BBP_HEADERS),
    body: bodyFromRows(sections?.bbpRows),
  });

  doc.save(ensureExtension(filename, ".pdf"));
}

function applyExcelBorders(sheet, rowNumber) {
  const row = sheet.getRow(rowNumber);
  for (let col = 1; col <= COLUMN_COUNT; col += 1) {
    row.getCell(col).border = border80;
  }
}

function writeExcelBanner(sheet, rowNumber, text, fillArgb) {
  sheet.mergeCells(rowNumber, 1, rowNumber, COLUMN_COUNT);
  const cell = sheet.getCell(rowNumber, 1);
  cell.value = text;
  cell.font = { bold: true, size: 12, name: "Arial" };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArgb } };
  applyExcelBorders(sheet, rowNumber);
  for (let col = 1; col <= COLUMN_COUNT; col += 1) {
    sheet.getCell(rowNumber, col).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fillArgb },
    };
  }
  sheet.getRow(rowNumber).height = 22;
}

function writeExcelHeaders(sheet, rowNumber, headers) {
  const headerRow = sheet.getRow(rowNumber);
  headerRow.height = 22;
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEBF7" } };
    cell.border = border80;
  });
}

function writeExcelDataRows(sheet, startRow, rows) {
  const list = rows && rows.length > 0 ? rows : [{ no: "", name: "", type: "", note: "" }];
  list.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(startRow + rowIndex);
    excelRow.height = 22;
    rowToCells(row).forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value || null;
      cell.font = { size: 11, name: "Arial" };
      cell.alignment = {
        horizontal: colIndex === 1 || colIndex === 3 ? "left" : "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
    });
  });
  return startRow + list.length;
}

export async function downloadMedicationXlsx(sections, siteNum, filename = "Medication_Management") {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("Medication");

  sheet.mergeCells(1, 1, 1, COLUMN_COUNT);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = buildMedicationTitle(siteNum, new Date());
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applyExcelBorders(sheet, 1);
  sheet.getRow(1).height = 28;

  writeExcelBanner(sheet, 2, CENTER_SECTION_TITLE, "FF9DC3E6");
  writeExcelHeaders(sheet, 3, CENTER_HEADERS);
  const afterCenter = writeExcelDataRows(sheet, 4, sections?.centerRows);

  writeExcelBanner(sheet, afterCenter, BBP_SECTION_TITLE, "FF9DC3E6");
  writeExcelHeaders(sheet, afterCenter + 1, BBP_HEADERS);
  writeExcelDataRows(sheet, afterCenter + 2, sections?.bbpRows);

  [5.5, 32, 16, 36].forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });

  sheet.pageSetup = {
    paperSize: 1,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
    margins: { left: 0.4, right: 0.4, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}
